import React from "react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";

interface ContributorListItem {
  slug: string;
  name: string;
  bio: string;
  bioSnippet: string;
  avatarUrl?: string;
  didOrPersonRef?: string;
  role: string;
  active: boolean;
  createdAt: string;
}

interface ListResponse {
  items: ContributorListItem[];
}

export function VoicesContributors() {
  const [items, setItems] = React.useState<ContributorListItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/voices/contributors");
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as ListResponse;
        if (!cancelled) setItems(data.items);
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Seo
        title="Contributors — Voices — The .person Foundation"
        description="The contributors writing on personhood, Emergent Intelligence, and the .person Protocol."
        path="/voices/contributors"
      />
      <PageShell
        title="Contributors"
        subtitle="The voices below write under their own bylines on personhood, Emergent Intelligence, Ubuntu, and the work of the .person Foundation."
        status="open"
      >
        <div className="mt-8">
          {error && (
            <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500 mb-8">
              Could not load contributors: {error}
            </div>
          )}

          {items === null && !error && (
            <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
              Loading…
            </p>
          )}

          {items && items.length === 0 && (
            <p className="font-serif text-lg text-ink-2 leading-relaxed max-w-2xl">
              The first contributors are being onboarded. The Foundation is calling.
            </p>
          )}

          {items && items.length > 0 && (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
              {items.map((c) => (
                <li key={c.slug}>
                  <article className="border-t border-hairline pt-6">
                    <h2 className="font-serif text-2xl text-ink mb-2">
                      <Link
                        to={`/voices/by/${c.slug}`}
                        className="hover:text-claude transition-colors"
                      >
                        {c.name}
                      </Link>
                    </h2>
                    {c.bioSnippet && (
                      <p className="font-serif text-base text-ink-2 leading-relaxed mb-3">
                        {c.bioSnippet}
                      </p>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-4 font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                      <Link
                        to={`/voices/by/${c.slug}`}
                        className="underline hover:text-ink"
                      >
                        Posts →
                      </Link>
                      {c.didOrPersonRef && (
                        <Link
                          to={c.didOrPersonRef}
                          className="underline hover:text-ink"
                        >
                          .person profile →
                        </Link>
                      )}
                    </div>
                  </article>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-16 font-mono text-[10px] text-ink-3 uppercase tracking-widest">
            <Link to="/voices" className="underline hover:text-ink">
              ← all voices
            </Link>
          </p>
        </div>
      </PageShell>
    </>
  );
}
